import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { generateId } from "../../utils/generateId";
import { RootState } from "../rootReducer";
import { ShoppingListItem, type Measure } from "../../models/shopping-list";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";
import { shoppingItemKey } from "../../utils/shoppingList";
import {
  categoriseShoppingItem,
  SHOPPING_CATEGORIES,
  type ShoppingCategoryKey,
} from "../../utils/shoppingCategories";
import { selectAllRecipes } from "./recipesSlice";
import { selectActiveListId, selectPrimaryListId } from "./shoppingListsSlice";
import { deleteShoppingListAction, mergeShoppingListsAction } from "../actions";

export const shoppingListAdapter = createEntityAdapter<ShoppingListItem>();
type ShoppingListState = EntityState<ShoppingListItem, string>;
const initialState: ShoppingListState = shoppingListAdapter.getInitialState();

type NewItem = {
  name: string;
  quantity?: string | null;
  measures?: Measure[];
  extras?: string[];
  recipeIds?: string[];
};

// Union two recipe-id lists (deduped); undefined args treated as empty.
const mergeRecipeIds = (a?: string[] | null, b?: string[] | null): string[] => [
  ...new Set([...(a ?? []), ...(b ?? [])]),
];

// Live items on one list, indexed by name (for de-dupe) with the running max
// sortOrder — both scoped to `listId` so each list orders and de-dupes on its own.
function listIndex(state: ShoppingListState, listId: string) {
  const idByName = new Map<string, string>();
  let maxSort = -1;
  for (const e of Object.values(state.entities)) {
    if (!e || e.deletedAt || e.listId !== listId) continue;
    if (e.sortOrder > maxSort) maxSort = e.sortOrder;
    idByName.set(shoppingItemKey(e.name), e.id); // ALL live items on the list
  }
  return { idByName, maxSort };
}

export const shoppingListSlice = createSlice({
  name: "shoppingListItems",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(shoppingListAdapter),
    removeManyFromSync: makeSyncRemove(shoppingListAdapter),

    addShoppingItem: {
      reducer(
        state: ShoppingListState,
        action: PayloadAction<{
          id: string;
          name: string;
          quantity?: string | null;
          listId: string;
        }>,
      ) {
        const { id, name, quantity, listId } = action.payload;
        shoppingListAdapter.addOne(state, {
          id,
          listId,
          name: name.trim(),
          quantity: quantity ?? undefined,
          checked: false,
          sortOrder: listIndex(state, listId).maxSort + 1,
        });
      },
      prepare(payload: NewItem & { listId: string }) {
        return { payload: { id: generateId(), ...payload } };
      },
    },

    // Add consolidated items to a list, skipping any name already on THAT list
    // (ticked or not) so an import never duplicates an existing line.
    addShoppingItems(
      state: ShoppingListState,
      action: PayloadAction<{ items: NewItem[]; listId: string }>,
    ) {
      const { items, listId } = action.payload;
      const { idByName, maxSort } = listIndex(state, listId);
      let sort = maxSort;

      for (const it of items) {
        const key = shoppingItemKey(it.name);
        if (!key || idByName.has(key)) continue; // blank or already present → skip
        const id = generateId();
        shoppingListAdapter.addOne(state, {
          id,
          listId,
          name: it.name.trim(),
          quantity: it.quantity ?? undefined,
          measures: it.measures ?? undefined,
          extras: it.extras ?? undefined,
          checked: false,
          sortOrder: ++sort,
          recipeIds: it.recipeIds ?? [],
        });
        idByName.set(key, id); // dedupe duplicate names within the same batch too
      }
    },

    // Re-add an import into a list: refresh + re-activate items already on it; add
    // the rest. Never duplicates. Powers "Re-add all".
    readdShoppingItems(
      state: ShoppingListState,
      action: PayloadAction<{ items: NewItem[]; listId: string }>,
    ) {
      const { items, listId } = action.payload;
      const { idByName, maxSort } = listIndex(state, listId);
      let sort = maxSort;

      for (const it of items) {
        const key = shoppingItemKey(it.name);
        if (!key) continue;
        const existingId = idByName.get(key);
        if (existingId) {
          const existing = state.entities[existingId];
          shoppingListAdapter.updateOne(state, {
            id: existingId,
            changes: {
              quantity: it.quantity ?? undefined,
              measures: it.measures ?? undefined,
              extras: it.extras ?? undefined,
              checked: false, // reactivate
              recipeIds: mergeRecipeIds(existing?.recipeIds, it.recipeIds),
            },
          });
        } else {
          const id = generateId();
          shoppingListAdapter.addOne(state, {
            id,
            listId,
            name: it.name.trim(),
            quantity: it.quantity ?? undefined,
            measures: it.measures ?? undefined,
            extras: it.extras ?? undefined,
            checked: false,
            sortOrder: ++sort,
            recipeIds: it.recipeIds ?? [],
          });
          idByName.set(key, id);
        }
      }
    },

    // Backfill: attach any item with no listId (legacy rows added before lists
    // existed) to the given (primary) list. Dirty-tracked so list_id pushes on sync.
    assignOrphanItemsToList(
      state: ShoppingListState,
      action: PayloadAction<{ listId: string }>,
    ) {
      const updates = Object.values(state.entities)
        .filter((i): i is ShoppingListItem => !!i && !i.deletedAt && !i.listId)
        .map((i) => ({ id: i.id, changes: { listId: action.payload.listId } }));
      shoppingListAdapter.updateMany(state, updates);
    },

    // Manually move an item to a grocery aisle (null → back to name-derived).
    setShoppingItemCategory(
      state: ShoppingListState,
      action: PayloadAction<{ id: string; category: ShoppingCategoryKey | null }>,
    ) {
      const { id, category } = action.payload;
      if (state.entities[id]) {
        shoppingListAdapter.updateOne(state, { id, changes: { category } });
      }
    },

    // Clear every manual aisle override on a list, dropping its items back to their
    // name-derived aisle.
    resetAllShoppingCategories(
      state: ShoppingListState,
      action: PayloadAction<{ listId: string }>,
    ) {
      const { listId } = action.payload;
      const updates = Object.values(state.entities)
        .filter(
          (i): i is ShoppingListItem =>
            !!i && !i.deletedAt && i.listId === listId && i.category != null,
        )
        .map((i) => ({ id: i.id, changes: { category: null as ShoppingCategoryKey | null } }));
      shoppingListAdapter.updateMany(state, updates);
    },

    toggleShoppingItem(state: ShoppingListState, action: PayloadAction<string>) {
      const item = state.entities[action.payload];
      if (item) {
        shoppingListAdapter.updateOne(state, {
          id: item.id,
          changes: { checked: !item.checked },
        });
      }
    },

    removeShoppingItem(state: ShoppingListState, action: PayloadAction<string>) {
      shoppingListAdapter.updateOne(state, {
        id: action.payload,
        changes: { deletedAt: new Date().toISOString() },
      });
    },

    // Soft-delete a list's checked items (the "clear ticked off" action).
    clearCheckedShoppingItems(
      state: ShoppingListState,
      action: PayloadAction<{ listId: string }>,
    ) {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter(
          (i): i is ShoppingListItem =>
            !!i && !i.deletedAt && i.listId === action.payload.listId && i.checked,
        )
        .map((i) => ({ id: i.id, changes: { deletedAt: now } }));
      shoppingListAdapter.updateMany(state, updates);
    },

    // Soft-delete every live item on a list (the "clear whole list" action).
    clearAllShoppingItems(
      state: ShoppingListState,
      action: PayloadAction<{ listId: string }>,
    ) {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter(
          (i): i is ShoppingListItem =>
            !!i && !i.deletedAt && i.listId === action.payload.listId,
        )
        .map((i) => ({ id: i.id, changes: { deletedAt: now } }));
      shoppingListAdapter.updateMany(state, updates);
    },

    // Un-delete a set of items — powers "Undo" after a clear. Clearing deletedAt
    // resurrects the row on the next sync (toRow sends deleted_at: null).
    restoreShoppingItems(state: ShoppingListState, action: PayloadAction<string[]>) {
      const updates = action.payload
        .filter((id) => state.entities[id]?.deletedAt)
        .map((id) => ({ id, changes: { deletedAt: undefined } }));
      shoppingListAdapter.updateMany(state, updates);
    },
  },
  extraReducers: (builder) => {
    // Cascade: soft-delete the items of a deleted list (off deleteShoppingListAction).
    builder.addCase(deleteShoppingListAction, (state, action) => {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter(
          (i): i is ShoppingListItem =>
            !!i && !i.deletedAt && i.listId === action.payload,
        )
        .map((i) => ({ id: i.id, changes: { deletedAt: now } }));
      shoppingListAdapter.updateMany(state, updates);
    });

    // Merge: re-parent every item on a merged-away list to the canonical list.
    builder.addCase(mergeShoppingListsAction, (state, action) => {
      const { fromIds, intoId } = action.payload;
      const from = new Set(fromIds);
      const updates = Object.values(state.entities)
        .filter(
          (i): i is ShoppingListItem =>
            !!i && !i.deletedAt && !!i.listId && from.has(i.listId),
        )
        .map((i) => ({ id: i.id, changes: { listId: intoId } }));
      shoppingListAdapter.updateMany(state, updates);
    });
  },
});

const shoppingSelectors = shoppingListAdapter.getSelectors(
  (s: RootState) => s.shoppingListItems,
);

// Live items on the ACTIVE list (item.listId, falling back to primary for legacy
// rows), unchecked first (by sortOrder), checked sink to the bottom. Everything
// downstream derives from this, so the whole shopping UI follows the active list.
export const selectAllShoppingItems = createSelector(
  shoppingSelectors.selectAll,
  selectActiveListId,
  selectPrimaryListId,
  (items, activeId, primaryId) =>
    items
      .filter((i) => !i.deletedAt && (i.listId ?? primaryId) === activeId)
      .sort((a, b) =>
        a.checked === b.checked ? a.sortOrder - b.sortOrder : a.checked ? 1 : -1,
      ),
);

export const selectShoppingCounts = createSelector(selectAllShoppingItems, (items) => ({
  total: items.length,
  checked: items.filter((i) => i.checked).length,
}));

// True when at least one item has a manual aisle override (gates the bulk reset).
export const selectHasAisleOverrides = createSelector(selectAllShoppingItems, (items) =>
  items.some((i) => i.category != null),
);

export interface ShoppingGroup {
  key: ShoppingCategoryKey;
  label: string;
  items: ShoppingListItem[];
}

// Live items bucketed into grocery aisles (in aisle order), empty aisles omitted.
// Within each aisle, unchecked come first (by sortOrder) and ticked-off items sink
// to the bottom of their own group.
const KNOWN_CATEGORY_KEYS = new Set<string>(SHOPPING_CATEGORIES.map((c) => c.key));

export const selectGroupedShoppingItems = createSelector(
  selectAllShoppingItems,
  (items): ShoppingGroup[] => {
    const byCat = new Map<ShoppingCategoryKey, ShoppingListItem[]>();
    for (const item of items) {
      // A manual override wins, but only if it's a category we still recognise;
      // otherwise (and when unset) fall back to deriving the aisle from the name.
      const key =
        item.category && KNOWN_CATEGORY_KEYS.has(item.category)
          ? item.category
          : categoriseShoppingItem(item.name);
      const bucket = byCat.get(key);
      if (bucket) bucket.push(item);
      else byCat.set(key, [item]);
    }

    const groups: ShoppingGroup[] = [];
    for (const cat of SHOPPING_CATEGORIES) {
      const bucket = byCat.get(cat.key);
      if (!bucket || bucket.length === 0) continue;
      bucket.sort((a, b) =>
        a.checked === b.checked ? a.sortOrder - b.sortOrder : a.checked ? 1 : -1,
      );
      groups.push({ key: cat.key, label: cat.label, items: bucket });
    }
    return groups;
  },
);

export interface ShoppingRecipeGroup {
  key: string; // recipeId, or "__manual__" for items with no known source recipe
  title: string;
  items: ShoppingListItem[];
}

// Sink checked items to the bottom of a group (unchecked first, by sortOrder).
const sinkChecked = (arr: ShoppingListItem[]): ShoppingListItem[] =>
  [...arr].sort((a, b) =>
    a.checked === b.checked ? a.sortOrder - b.sortOrder : a.checked ? 1 : -1,
  );

// Live items grouped by their source recipe(s). An item shared by several recipes
// appears under each of them (same underlying row — ticking it anywhere ticks it
// everywhere). Items with no known source recipe (manual, or all sources deleted)
// collect in an "Added manually" group at the end. Recipe groups are title-sorted.
export const selectShoppingItemsByRecipe = createSelector(
  selectAllShoppingItems,
  selectAllRecipes,
  (items, recipes): ShoppingRecipeGroup[] => {
    const titleById = new Map(recipes.map((r) => [r.id, r.title?.trim() || "Untitled recipe"]));
    const byRecipe = new Map<string, ShoppingListItem[]>();
    const manual: ShoppingListItem[] = [];

    for (const item of items) {
      const ids = (item.recipeIds ?? []).filter((id) => titleById.has(id));
      if (ids.length === 0) {
        manual.push(item);
        continue;
      }
      for (const id of ids) {
        const bucket = byRecipe.get(id);
        if (bucket) bucket.push(item);
        else byRecipe.set(id, [item]);
      }
    }

    const groups: ShoppingRecipeGroup[] = [...byRecipe.keys()]
      .sort((a, b) => titleById.get(a)!.localeCompare(titleById.get(b)!))
      .map((id) => ({ key: id, title: titleById.get(id)!, items: sinkChecked(byRecipe.get(id)!) }));

    if (manual.length) {
      groups.push({ key: "__manual__", title: "Added manually", items: sinkChecked(manual) });
    }
    return groups;
  },
);

export const {
  addShoppingItem,
  addShoppingItems,
  assignOrphanItemsToList,
  setShoppingItemCategory,
  resetAllShoppingCategories,
  toggleShoppingItem,
  removeShoppingItem,
  clearCheckedShoppingItems,
  clearAllShoppingItems,
  restoreShoppingItems,
  readdShoppingItems,
  upsertManyFromSync: upsertShoppingListFromSync,
  removeManyFromSync: removeShoppingListFromSync,
} = shoppingListSlice.actions;

export default shoppingListSlice.reducer;
